/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import ReactQuill from "react-quill";

import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";

import MDButton from "components/MDButton";

import { AddBox } from "@mui/icons-material";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";

import { Loading } from "notiflix/build/notiflix-loading-aio";
import { Notify } from "notiflix/build/notiflix-notify-aio";

import Typography from "@mui/material/Typography";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";

import ControlledTextInput from "components/FormInputs/ControlledTextInput";

//components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import IconButton from "@mui/material/IconButton";

import Box from "@mui/material/Box";

// theme CSS for React CSV Importer
import "react-csv-importer/dist/index.css";

// queries
import {
  usePostServerProductMutation,
  useLazyGetAllServerProductsByCountryIdQuery,
} from "services/products";

import { useUploadFileMutation } from "services/files";

import { globalCountry, setSelectedCountryURL } from "store/entities/countries";

import {
  replaceSingleQuoteToSQLFriendly,
  replaceHTMLEntityAnd,
  removeLocalStorageItemsWithPrefix,
  addSpacesToNames,
} from "utils/helperFunctions";

import Alert from "components/CustomAlert";

import CircularProgress from "@mui/material/CircularProgress";

import CustomTabs from "components/CustomTabs";

import { ArrowBack } from "@mui/icons-material";

import { addServerProductSchema } from "../schemas/_model";

// ReactQuill modules
const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  },
};

function AddServerProduct() {
  const initialFormState = {
    price: 0,
    name: "",
    averageRating: 0,
    shortDescription: "",
    sku: "",
    options: [
      {
        category: "",
        name: "",
        price: 0,
        min_quantity: 0,
        max_quantity: 0,
      },
    ],
  };

  const navigate = useHistory();

  const selectedCountry = useSelector(globalCountry);

  const [editorDescriptionOnChange, setEditorDescriptionOnChange] = useState("");

  const [file, setFile] = useState([]);

  const [uploadFile, { isLoading: isFileUploading }] = useUploadFileMutation();

  const [postProduct] = usePostServerProductMutation();

  const [triggerProducts] = useLazyGetAllServerProductsByCountryIdQuery();

  const dispatch = useDispatch();

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    resetField,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(addServerProductSchema),
    defaultValues: initialFormState,
  });

  const {
    fields: optionsFields,
    append: appendOptions,
    remove: removeOptions,
  } = useFieldArray({
    control,
    name: "options",
  });

  const onSubmit = async (data) => {
    if (file.length == 0) {
      Notify.failure("You must add atleast one product image!");
      return;
    }

    if (editorDescriptionOnChange === undefined || editorDescriptionOnChange == "") {
      Notify.failure("You must add the product description!");
      return;
    }

    // setting up the loader

    Loading.dots({
      svgColor: "rgba(159, 3, 3, 0.8)",
    });

    const imagePath = [];

    if (file && file.length > 0) {
      for (let image of file) {
        let formData = new FormData();
        formData.append("file", image);
        let imageResponse = await uploadFile({
          formData: formData,
          directory: "product_images",
        });
        if (imageResponse.data.status == "success") {
          imagePath.push(imageResponse.data?.response.path.replace(/\\/g, "/"));
        }
      }
    }

    if (imagePath && imagePath.length > 0) {
      let description = editorDescriptionOnChange ? editorDescriptionOnChange : "<p>&nbsp; </p>";

      data.description = replaceSingleQuoteToSQLFriendly(description);
      data.countryId = selectedCountry.id;
      data.images = imagePath;

      console.log(data);

      try {
        const parentProduct = await postProduct(data);

        if (parentProduct.data?.status == "success") {
          Notify.success("Product has been added successfully!");
          triggerProducts(selectedCountry.id);
          navigate.push("/user/server-products");
        } else {
          Notify.failure("Something went wrong. Please try again!");
        }
      } catch (e) {
      } finally {
        Loading.remove(500);
      }
    } else {
      Notify.failure(
        "There was an issue uploading the product image, please check again in few minutes!"
      );
      // Loading.remove(500);
    }
  };

  const handleUpdateFiles = (files) => {
    if (files.length > 0) {
      let photos = [];
      for (let file of files) {
        photos.push(file.source);
      }
      setFile(photos);
    } else {
      setFile([]);
    }
  };

  useEffect(() => {
    console.log(errors);
  }, [errors]);

  return (
    <Card>
      <CardHeader
        // avatar={<AccountCircleIcon fontSize="large" color="dark" />}
        title={<MDTypography color="info">Add Server Product</MDTypography>}
        action={
          <IconButton color={"secondary"} onClick={() => navigate.goBack()} size="large">
            <ArrowBack />
          </IconButton>
        }
      />
      <CardContent>
        <form>
          <GridContainer>
            <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
              <ControlledTextInput
                name="name"
                control={control}
                labelText="Name *"
                formControlProps={{
                  fullWidth: true,
                }}
              />
            </GridItem>
            <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
              <ControlledTextInput
                name="sku"
                control={control}
                labelText="SKU"
                formControlProps={{
                  fullWidth: true,
                }}
              />
            </GridItem>
            <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
              <ControlledTextInput
                name="price"
                control={control}
                labelText="Enter Base Price *"
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  type: "number",
                }}
              />
            </GridItem>
            <GridItem xs={12} sm={12}>
              <Box
                sx={{
                  border: "0.0625rem solid #f0f2f5",
                  padding: "10px",
                  mb: 3,
                  borderRadius: "0.375rem",
                }}
              >
                <Box display="flex" justifyContent="space-between" my={1}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      color: "#3f3f3f",
                    }}
                  >
                    Options
                  </Typography>
                  <div>
                    <MDButton
                      color="info"
                      variant="gradient"
                      iconOnly
                      onClick={() => {
                        appendOptions({
                          category: "",
                          name: "",
                          price: 0,
                        });
                      }}
                    >
                      <AddBox sx={{}} />
                    </MDButton>
                  </div>
                </Box>
                <br />
                {optionsFields.map((input, index) => {
                  return (
                    <GridContainer key={index}>
                      <GridItem xs={12} sm={11}>
                        <GridContainer key={index}>
                          <GridItem xs={12} sm={12} md={6} lg={6} xl={6}>
                            <ControlledTextInput
                              id="category_key"
                              name={`options.${index}.category`}
                              control={control}
                              labelText="Category *"
                              formControlProps={{
                                fullWidth: true,
                              }}
                            />
                          </GridItem>

                          <GridItem xs={12} sm={12} md={6} lg={6} xl={6}>
                            <ControlledTextInput
                              id="name_key"
                              name={`options.${index}.name`}
                              control={control}
                              labelText="Name *"
                              formControlProps={{
                                fullWidth: true,
                              }}
                            />
                          </GridItem>
                        </GridContainer>
                        <GridContainer key={index}>
                          <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                            <ControlledTextInput
                              id={`price_key_${index}`}
                              name={`options.${index}.price`}
                              control={control}
                              labelText="Price *"
                              formControlProps={{
                                fullWidth: true,
                              }}
                              inputProps={{
                                type: "number",
                              }}
                            />
                          </GridItem>
                          <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                            <ControlledTextInput
                              id={`min_quantity_key_${index}`}
                              name={`options.${index}.min_quantity`}
                              control={control}
                              labelText="Min Quantity *"
                              formControlProps={{
                                fullWidth: true,
                              }}
                              inputProps={{
                                type: "number",
                              }}
                            />
                          </GridItem>
                          <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                            <ControlledTextInput
                              id={`max_quantity_key_${index}`}
                              name={`options.${index}.max_quantity`}
                              control={control}
                              labelText="Max Quantity *"
                              formControlProps={{
                                fullWidth: true,
                              }}
                              inputProps={{
                                type: "number",
                              }}
                            />
                          </GridItem>
                        </GridContainer>
                      </GridItem>
                      <GridItem
                        xs={12}
                        sm={1}
                        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <MDButton
                          color="warning"
                          variant="gradient"
                          iconOnly
                          onClick={() => removeOptions(index)}
                        >
                          <IndeterminateCheckBoxIcon />
                        </MDButton>
                      </GridItem>
                    </GridContainer>
                  );
                })}
              </Box>
            </GridItem>
            <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
              <ControlledTextInput
                multiline={true}
                name="shortDescription"
                control={control}
                labelText="Short description *"
                formControlProps={{
                  fullWidth: true,
                }}
              />
            </GridItem>
            <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
              <MDBox
                sx={{
                  border: "1px solid #d2d6da",
                  borderRadius: "0.375rem",
                  mb: 3,
                  pt: 2,
                  position: "relative",
                  // "& .tox-tinymce": { border: "none", position: "relative" },
                  "& .ql-editor": { minHeight: "300px" },
                }}
              >
                <MDTypography
                  component="p"
                  color="info"
                  sx={{
                    lineHeight: "1.5",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    transform: "translate(7px, -50%)",
                    backgroundColor: "#fff",
                    color: "#7b809a",
                    padding: "0 5px",
                    position: "absolute",
                    left: 0,
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  Description
                </MDTypography>
                {/* <TinyMCEEditor initialContent={""} realTimeContent={setEditorDescriptionOnChange} /> */}
                <ReactQuill
                  theme="snow"
                  value={editorDescriptionOnChange}
                  onChange={setEditorDescriptionOnChange}
                  modules={modules}
                />
              </MDBox>
            </GridItem>

            <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
              <ControlledTextInput
                name="averageRating"
                control={control}
                labelText="Average rating *"
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  type: "number",
                }}
              />
            </GridItem>

            <GridItem xs={12} sm={12}>
              <>
                <Box
                  component={FilePond}
                  sx={{
                    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
                    "& .filepond--file-action-button": {
                      cursor: "pointer",
                    },
                    "& .filepond--drop-label.filepond--drop-label label": {
                      fontSize: "13px",
                      letterSpacing: "0.5px",
                      fontWeight: "600",
                    },
                    mt: 2,
                  }}
                  instantUpload={false}
                  allowFileSizeValidation={true}
                  name="file"
                  allowFileTypeValidation={true}
                  acceptedFileTypes={[
                    "image/jpeg",
                    "image/jpg",
                    "image/png",
                    "image/webp",
                    "application/msword", // for .doc
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // for .docx
                    "application/pdf",
                  ]}
                  allowMultiple={true}
                  allowFilePoster={false}
                  labelIdle={
                    'Drag & Drop product images or <span class="filepond--label-action">Browse</span>'
                  }
                  onupdatefiles={handleUpdateFiles}
                  allowRevert={false}
                />
              </>
            </GridItem>
          </GridContainer>
        </form>
        <CardActions disableSpacing>
          * Required fields
          <Box sx={{ marginLeft: "auto" }}>
            <MDButton
              color="primary"
              variant="gradient"
              id="submit"
              onClick={handleSubmit(onSubmit)}
            >
              Add Server Product
            </MDButton>
            <MDButton
              color="secondary"
              variant="gradient"
              onClick={() => navigate.goBack()}
              sx={{ ml: 1 }}
            >
              Cancel
            </MDButton>
          </Box>
        </CardActions>
      </CardContent>
    </Card>
  );
}

export default AddServerProduct;
